import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Modal,
  OutlinedInput,
  Select,
  Typography,
  Chip,
  FormControl,
  AccordionDetails,
  AccordionProps,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material";
import { FontGrayNormal, ModalTitle } from "../modals/style";
import { CustomButton, StyledDivider } from "../../globalStyle";
import styled, { useTheme } from "styled-components";
import { FolderOpenIcon } from "../../assets/Icons";
import { useDispatch, useSelector } from "react-redux";
import {
  folderAccessMemberListSelector,
  folderItemSelector,
  folderListsSelector,
  isLoadingSelector,
  isRemoveUserModalOpenSelector,
  isTeamListLoadingSelector,
  membersListSelector,
  removeTeamMemberSelector,
  selectedSharedFolderSelector,
  selectedTeamFolderLIst,
} from "../../store/metadata/selector";
import React, { useEffect, useMemo, useState } from "react";
import { MetaDataSagaActions } from "../../store/metadata/sagas";
import {
  FolderAccessMembersType,
  FolderTeamMembersType,
} from "../../store/metadata/types";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { MetaDataReducerAction } from "../../store/metadata/slice";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Groups, Person } from "@mui/icons-material";

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `1px solid #EAECEE`,
  borderRadius: "8px",
  "&:not(:last-child)": {
    marginBottom: 2,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
    {...props}
  />
))(() => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: 5,
  },
}));

const ErrorMessage = ({ children }: { children: React.ReactNode }) => (
  <Typography
    color="error"
    variant="caption"
    sx={{ mt: 0.5, display: "block" }}
  >
    {children}
  </Typography>
);

const ShareAccessToTeamAndUsersModal = () => {
  const theme = useTheme();
  const { folderId: routeFolderId } = useParams<{ folderId?: string }>();
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [isEmailError, setIsEmailError] = useState(false);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<
    string | null
  >(null);

  const openAddUserModal = useSelector(isRemoveUserModalOpenSelector);
  const shareType = openAddUserModal?.shareType ?? null;
  const [activeShareType, setActiveShareType] = useState<
    "team" | "user" | null
  >(shareType);

  const removeId = useSelector(removeTeamMemberSelector);
  const selectedFolder = useSelector(folderItemSelector);
  const selectedSharedFolder = useSelector(selectedSharedFolderSelector);
  const myTeamList = useSelector(selectedTeamFolderLIst);
  const folderListData = useSelector(folderListsSelector);
  const folderAccessMemberList: FolderAccessMembersType = useSelector(
    folderAccessMemberListSelector
  );

  const folderTeams = useMemo(() => {
    const teamsData: any = folderAccessMemberList?.teams;
    if (Array.isArray(teamsData)) {
      return teamsData;
    }
    if (teamsData && Array.isArray(teamsData._id)) {
      const ids = teamsData._id as string[];
      const names = teamsData.name || [];
      const descriptions = teamsData.description || [];
      const createdAt = teamsData.createdAt || [];
      const membersObj = teamsData.members || {};
      return ids.map((id: string, index: number) => ({
        _id: id,
        name: names[index] || '',
        description: descriptions[index] || '',
        createdAt: createdAt[index] || '',
        members:
          (membersObj?.userEmail || []).map((email: string, memberIndex: number) => ({
            userEmail: email,
            accepted: membersObj?.accepted?.[memberIndex] || false,
            isOwner: membersObj?.isOwner?.[memberIndex] || false,
            _id: membersObj?._id?.[memberIndex] || '',
            fullName: membersObj?.fullName?.[memberIndex] || '',
            profileImage: membersObj?.profileImage?.[memberIndex] || '',
          })) || [],
      }));
    }
    return [];
  }, [folderAccessMemberList]);
  const folderMembers = folderAccessMemberList?.members || [];

  const [expanded, setExpanded] = useState<string | false>(false);
  const isLoadingIndividualAdd = useSelector(isLoadingSelector);
  const teamSelectLoading = useSelector(isTeamListLoadingSelector);
  const membersList = useSelector(membersListSelector);

  // Update activeShareType when shareType prop changes
  useEffect(() => {
    setActiveShareType(shareType);
  }, [shareType]);

  const currentFolderId = openAddUserModal?.folderId || routeFolderId || selectedFolder?._id || selectedSharedFolder?._id;
  


  const folderNameFromList = useMemo(
    () =>
      folderListData?.userFolders.find(
        (folder: any) => folder._id === routeFolderId
      )?.name,
    [routeFolderId, folderListData?.userFolders]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      teamIds: [] as string[],
    },
  });

  // Filtered teams - exclude teams that are already shared with the folder
  const availableTeamsForSharing = useMemo(() => {
    if (!myTeamList || !Array.isArray(myTeamList)) return [];

    const currentTeamIds = folderTeams.map((team) => team._id);
    return myTeamList.filter((team: any) => !currentTeamIds.includes(team._id));
  }, [myTeamList, folderTeams]);

  // Get all emails of users who already have access (direct members + team members)
  const existingUserEmails = useMemo(() => {
    const emails = new Set<string>();

    // Add direct folder members
    folderMembers.forEach((member) => {
      if (member.email) emails.add(member.email.toLowerCase());
    });

    // Add team members
    folderTeams.forEach((team) => {
      team.members?.forEach((member: any) => {
        if (member.userEmail && typeof member.userEmail === 'string') {
          emails.add(member.userEmail.toLowerCase());
        }
      });
    });

    return emails;
  }, [folderMembers, folderTeams]);

  useEffect(() => {
    dispatch(MetaDataSagaActions.getInviteMembersList());
  }, [dispatch]);

  useEffect(() => {
    if (openAddUserModal?.isOpen && currentFolderId) {
      dispatch(
        MetaDataSagaActions.fetchFolderAccessMembersList({
          folderId: currentFolderId,
        })
      );
      dispatch(
        MetaDataReducerAction.setFolderTeamMemberList({ data: [], total: 0 })
      );

      // Only fetch team member list if we're in user mode
      if (activeShareType === "user") {
        dispatch(
          MetaDataSagaActions.fetchFolderTeamMembersList({
            folderId: currentFolderId,
            search: searchText,
          })
        );
      }

      // Only fetch team list if we're in team mode
      if (activeShareType === "team") {
        dispatch(MetaDataSagaActions.getSelectTeamBy());
      }
    }
  }, [
    openAddUserModal?.isOpen,
    currentFolderId,
    searchText,
    activeShareType,
    dispatch,
  ]);

  // Fetch team list on component mount for team selection
  useEffect(() => {
    dispatch(MetaDataSagaActions.getSelectTeamBy());
  }, [dispatch]);

  const sendInviteHandler = (value?: string) => {
    setIsEmailError(false);
    dispatch(
      MetaDataSagaActions.sendFolderInvite({
        folderId: currentFolderId,
        userEmail: value,
      })
    );
  };

  const onShareWithTeamSubmit = (formData: { teamIds: string[] }) => {
    const { teamIds } = formData;

    
    if (!currentFolderId) {
      console.error("Folder ID is missing");
      return;
    }
    
    if (!teamIds || teamIds.length === 0) {
      console.warn("No teams selected");
      return;
    }

    dispatch(
      MetaDataSagaActions.shareFolderWithTeam({
        folderId: currentFolderId,
        teamIds: teamIds,
      })
    );
    
    // Refresh the folder access list to show updated teams
    dispatch(
      MetaDataSagaActions.fetchFolderAccessMembersList({
        folderId: currentFolderId,
      })
    );
    
    dispatch(MetaDataSagaActions.fetchSharedFolderList());
    
    if (currentFolderId && selectedFolder) {
      dispatch(
        MetaDataSagaActions.fetchFolderList({
          selectedFolderId: currentFolderId,
          selectedBoardId: selectedFolder?.boards[0]?._id,
        })
      );
    }
    
    reset({ teamIds: [] });
  };

  // Filtered members list - exclude users who already have access
  const RenderTeamMembersListComponent = useMemo(() => {
    if (activeShareType !== "user") return null;

    const filteredMembersList = membersList?.members
      ?.filter(
        (item: FolderTeamMembersType) =>
          item.email && !existingUserEmails.has(item.email.toLowerCase())
      )
      ?.filter(
        (item: FolderTeamMembersType) =>
          item?.email?.toLowerCase()?.includes(searchText?.toLowerCase()) ||
          item?.fullName?.toLowerCase()?.includes(searchText?.toLowerCase())
      );

    return filteredMembersList?.length ? (
      <>
        {filteredMembersList?.map(
          (item: FolderTeamMembersType, index: number) => (
            <Grid
              item
              xs={12}
              key={item?._id}
              sx={{
                paddingBottom: "15px",
                borderBottom:
                  filteredMembersList?.length - 1 !== index
                    ? `1px solid ${theme.colors.gallery}`
                    : "none",
                marginBottom:
                  filteredMembersList?.length - 1 !== index ? "15px" : "0",
              }}
            >
              <Box
                sx={{ display: "flex", gap: "0.857rem", alignItems: "center" }}
              >
                <Avatar
                  src={item?.profileImage || "assests/images/user-logo.png"}
                  sx={{ width: "2.5rem", height: "2.5rem" }}
                />
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography fontSize="1rem" fontWeight="500" color="black">
                      {item?.fullName}
                    </Typography>
                    <Typography
                      fontSize="0.875rem"
                      fontWeight="400"
                      color={theme.colors.gray1}
                    >
                      {item?.email}
                    </Typography>
                  </Box>
                  <CustomButton
                    onClick={() => {
                      sendInviteHandler(item?.email);
                      setSelectedTeamMemberId(item?._id);
                    }}
                    disabled={
                      isLoadingIndividualAdd &&
                      selectedTeamMemberId === item?._id
                    }
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: theme.colors.black2,
                      borderRadius: "20px",
                      border: "1px solid #EAECEE",
                      padding: "8px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {isLoadingIndividualAdd &&
                      selectedTeamMemberId === item?._id && (
                        <CircularProgress size={16} sx={{ mr: 0.5 }} />
                      )}
                    Add
                  </CustomButton>
                </Box>
              </Box>
            </Grid>
          )
        )}
      </>
    ) : (
      <Box
        sx={{
          minWidth: "fit-content",
          textAlign: "center",
          color: theme.colors.gray1,
          p: 3,
          borderRadius: "8px",
          background: "rgba(0,0,0,0.02)",
        }}
      >
        <Person sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
        <Typography fontSize="1rem" fontWeight="600" sx={{ mb: 1 }}>
          {searchText
            ? "No matching members found"
            : "All members already have access"}
        </Typography>
        <Typography fontSize="0.875rem" sx={{ opacity: 0.7 }}>
          {searchText
            ? "Try searching with different keywords"
            : "All available members already have access to this folder"}
        </Typography>
      </Box>
    );
  }, [
    searchText,
    isLoadingIndividualAdd,
    selectedTeamMemberId,
    activeShareType,
    existingUserEmails,
    theme.colors.black2,
    theme.colors.gallery,
    theme.colors.gray1,
    membersList?.members,
  ]);

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const removeMember = (email: string, id: string) => {
    if (currentFolderId) {
      dispatch(
        MetaDataSagaActions.removeTeamMember({
          userEmail: email,
          folderId: currentFolderId,
          userId: id,
        })
      );
    }
  };

  const removeTeam = (teamId: string) => {
    if (currentFolderId) {
      dispatch(
        MetaDataSagaActions.removeTeamFromAccess({
          teamId,
          folderId: currentFolderId,
        })
      );
    }
  };

  const handleModalClose = () => {
    dispatch(
      MetaDataReducerAction.setIsRemoveUserModalOpen({
        isOpen: false,
        isUser: false,
        shareType: null,
        folderId: null,
      })
    );
    reset({ teamIds: [] });
    setSearchText("");
    setIsEmailError(false);
    setSelectedTeamMemberId(null);
  };

  const currentModalTitle =
    routeFolderId && routeFolderId === selectedSharedFolder?._id
      ? selectedSharedFolder?.name
      : openAddUserModal?.isUser
      ? folderNameFromList
      : selectedFolder?.name;

  // Determine what to show in the shared list based on activeShareType
  const shouldShowTeams = activeShareType === "team" && folderTeams?.length > 0;
  const shouldShowMembers =
    activeShareType === "user" && folderMembers?.length > 0;
  return (
    <Modal
      open={!!openAddUserModal?.isOpen}
      onClose={handleModalClose}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        outline: "none",
      }}
    >
      <Box
        display="flex"
        sx={{
          minWidth: { xs: "95%", md: "1000px" },
          maxWidth: "1000px",
          background: "white",
          borderRadius: "20px",
          minHeight: "40vh",
          maxHeight: "90vh",
          flexDirection: { xs: "column", md: "row" },
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {/* Left Pane: Shared With List */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            padding: "24px",
            borderRight: {
              xs: "none",
              md: `1px solid ${theme.colors.gallery}`,
            },
            borderBottom: {
              xs: `1px solid ${theme.colors.gallery}`,
              md: "none",
            },
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            background: "linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%)",
          }}
        >
          <ModalTitle
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
          >
            <FolderOpenIcon />
            {currentModalTitle || "Share Folder"}
          </ModalTitle>
          <FontGrayNormal sx={{ mb: 3 }}>
            Currently shared with{" "}
            {activeShareType === "team" ? "teams" : "members"}:
          </FontGrayNormal>

          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
            {/* Teams Section */}
            {shouldShowTeams && (
              <>
                <Typography
                  fontWeight="600 !important"
                  fontSize="1rem !important"
                  mb={2}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Groups fontSize="small" />
                  Teams ({folderTeams.length})
                </Typography>
                {Array.isArray(folderTeams) && folderTeams.map((team) => (
                  <Accordion
                    key={team._id}
                    expanded={expanded === team._id}
                    onChange={handleChange(team._id)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              width: "24px",
                              height: "24px",
                              fontSize: "12px",
                              color: theme.colors.white,
                              borderRadius: "50%",
                              background:
                                "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              sx={{ fontWeight: 600, fontSize: "11px" }}
                            >
                              {team.members?.length || 0}
                            </Typography>
                          </Box>
                          <Typography fontWeight={600}>{team.name}</Typography>
                        </Box>
                        <CustomButton
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTeam(team._id);
                          }}
                          disabled={removeId === team._id}
                          sx={{
                            padding: "8px 12px",
                            borderRadius: "20px",
                            border: "1px solid #EAECEE",
                            color: "#414042",
                            fontWeight: 500,
                          }}
                        >
                          {removeId === team._id && (
                            <CircularProgress size={16} sx={{ mr: 0.5 }} />
                          )}
                          Remove
                        </CustomButton>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, py: 1.5 }}>
                      {(team.members || []).map((member: any, idx: number) => (
                        <React.Fragment key={member._id}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              mb: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: "0.857rem",
                                alignItems: "center",
                              }}
                            >
                              <Avatar
                                src={
                                  member?.profileImage ||
                                  "assets/images/user-logo.png"
                                }
                                sx={{ width: "2.5rem", height: "2.5rem" }}
                              />
                              <Box>
                                <Typography
                                  fontSize="1rem"
                                  fontWeight="500"
                                  color="black"
                                >
                                  {member?.fullName || "No Name"}
                                </Typography>
                                <Typography
                                  fontSize="0.875rem"
                                  fontWeight="400"
                                  color={theme.colors.gray1}
                                >
                                  {member?.userEmail}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          {team.members?.length - 1 !== idx && (
                            <Box sx={{ width: "100%", mb: "7px" }}>
                              <StyledDivider />
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            )}

            {/* Members Section */}
            {shouldShowMembers && (
              <>
                <Typography
                  fontWeight="600 !important"
                  fontSize="1rem !important"
                  mb={2}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Person fontSize="small" />
                  Members ({folderMembers.length})
                </Typography>
                {folderMembers?.map((item, index) => (
                  <React.Fragment key={item._id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        mb: 1.5,
                        p: 2,
                        borderRadius: "12px",
                        background: "rgba(255,255,255,0.7)",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: "0.857rem",
                          alignItems: "center",
                        }}
                      >
                        <Avatar
                          src={
                            item?.profileImage || "assests/images/user-logo.png"
                          }
                          sx={{ width: "2.5rem", height: "2.5rem" }}
                        />
                        <Box>
                          <Typography
                            fontSize="1rem"
                            fontWeight="500"
                            color="black"
                          >
                            {item?.fullName}
                          </Typography>
                          <Typography
                            fontSize="0.875rem"
                            fontWeight="400"
                            color={theme.colors.gray1}
                          >
                            {item?.email}
                          </Typography>
                        </Box>
                      </Box>
                      {item?.canDelete && (
                        <CustomButton
                          onClick={() => removeMember(item?.email, item?._id)}
                          disabled={removeId === item?._id}
                          sx={{
                            padding: "8px 12px",
                            borderRadius: "20px",
                            border: "1px solid #EAECEE",
                            color: "#414042",
                            fontWeight: 500,
                          }}
                        >
                          {removeId === item?._id && (
                            <CircularProgress size={16} sx={{ mr: 0.5 }} />
                          )}
                          Remove
                        </CustomButton>
                      )}
                    </Box>
                  </React.Fragment>
                ))}
              </>
            )}

            {!shouldShowTeams && !shouldShowMembers && (
              <Box
                sx={{
                  textAlign: "center",
                  color: theme.colors.gray1,
                  mt: 4,
                  p: 3,
                  borderRadius: "12px",
                  background: "rgba(0,0,0,0.02)",
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Not shared yet
                </Typography>
                <Typography variant="body2">
                  Start sharing with{" "}
                  {activeShareType === "team" ? "teams" : "members"} using the
                  panel on the right.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Pane: Add Users/Teams */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            padding: "24px",
            minHeight: "50%",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
          }}
        >
          <ModalTitle sx={{ mb: 1 }}>
            Share {activeShareType === "team" ? "with Teams" : "with Members"}
          </ModalTitle>
          <FontGrayNormal sx={{ mb: 3 }}>
            {activeShareType === "team"
              ? "Share this folder with teams to give all team members access."
              : "Share this folder with individual members to give them access."}
          </FontGrayNormal>

          {/* Team Sharing Section */}
          {activeShareType === "team" && (
            <form onSubmit={handleSubmit(onShareWithTeamSubmit)}>
              <Typography
                variant="subtitle1"
                fontWeight="500"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Groups fontSize="small" />
                Select Teams ({availableTeamsForSharing.length} available)
              </Typography>
              <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                <FormControl fullWidth sx={{ marginRight: "1rem" }}>
                  <Controller
                    name="teamIds"
                    control={control}
                    rules={{
                      required:
                        availableTeamsForSharing.length > 0
                          ? "Please select at least one team to share."
                          : undefined,
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        multiple
                        displayEmpty
                        disabled={availableTeamsForSharing.length === 0}
                        input={
                          <OutlinedInput
                            placeholder={
                              availableTeamsForSharing.length === 0
                                ? "No teams available to share"
                                : "Select teams"
                            }
                            notched={false}
                          />
                        }
                        renderValue={(selectedTeamIds: string[]) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selectedTeamIds.map((id) => {
                              const team = availableTeamsForSharing?.find(
                                (t: any) => t._id === id
                              );
                              return (
                                <Chip
                                  key={id}
                                  label={
                                    team
                                      ? team?.name
                                      : `ID: ${id.substring(0, 6)}...`
                                  }
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onDelete={() => {
                                    const updatedTeamIds = field.value.filter(
                                      (itemId: string) => itemId !== id
                                    );
                                    field.onChange(updatedTeamIds);
                                  }}
                                  size="small"
                                  sx={{
                                    borderRadius: "8px",
                                    background:
                                      "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                                    color: "white",
                                  }}
                                />
                              );
                            })}
                            {selectedTeamIds.length === 0 && (
                              <Typography
                                sx={{
                                  color: theme.colors.gray1,
                                  opacity: 0.7,
                                  fontSize: "0.9rem",
                                  pl: 0.5,
                                }}
                              >
                                {availableTeamsForSharing.length === 0
                                  ? "All teams already have access"
                                  : "Select teams"}
                              </Typography>
                            )}
                          </Box>
                        )}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              borderRadius: 15,
                              maxHeight: "300px",
                              overflowY: "auto",
                            },
                          },
                        }}
                        sx={{
                          borderRadius: "12px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#EAECEE",
                          },
                          "& .MuiInputBase-input": {
                            padding: "14px 16px !important",
                          },
                        }}
                      >
                        {availableTeamsForSharing?.map((team: any) => (
                          <MenuItem key={team._id} value={team._id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Groups fontSize="small" />
                              {team.name}
                            </Box>
                          </MenuItem>
                        ))}
                        {availableTeamsForSharing.length === 0 && (
                          <MenuItem disabled>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Groups fontSize="small" />
                              All teams already have access
                            </Box>
                          </MenuItem>
                        )}
                      </Select>
                    )}
                  />
                  {errors.teamIds && (
                    <ErrorMessage>{errors.teamIds.message}</ErrorMessage>
                  )}
                </FormControl>
                <CustomButton
                  type="submit"
                  disabled={
                    teamSelectLoading || availableTeamsForSharing.length === 0
                  }
                  style={{
                    width: "120px",
                    fontSize: "14px",
                    color: theme.colors.white,
                    borderRadius: "12px",
                    border: "none",
                    background:
                      availableTeamsForSharing.length === 0
                        ? "#ccc"
                        : "linear-gradient(94.04deg, #1c75bc 2.44%, #00aeef 112.73%)",
                    padding: "14px 20px",
                    height: "50px",
                    boxShadow:
                      availableTeamsForSharing.length === 0
                        ? "none"
                        : "0 4px 12px rgba(28, 117, 188, 0.3)",
                  }}
                >
                  {teamSelectLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    "Share"
                  )}
                </CustomButton>
              </Box>
            </form>
          )}

          {/* Member Sharing Section */}
          {activeShareType === "user" && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight="500"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <Person fontSize="small" />
                Search Members
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                width="100%"
                sx={{ mb: 3 }}
              >
                <OutlinedInput
                  fullWidth
                  type="search"
                  value={searchText}
                  error={isEmailError}
                  placeholder="Search members by name or email..."
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  sx={{
                    borderRadius: "12px",
                    background: "#F6F7F8 !important",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: isEmailError
                        ? theme.palette.error.main
                        : "#EAECEE",
                    },
                    input: {
                      padding: "14px 16px",
                      "&::placeholder": {
                        color: theme.colors.gray1,
                        opacity: 0.7,
                      },
                    },
                  }}
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                gap="10px"
                sx={{ flexGrow: 1, overflow: "hidden" }}
              >
                <Box
                  border="1px solid #EAECEE"
                  borderRadius="12px"
                  display="flex"
                  flexDirection="column"
                  padding="20px"
                  sx={{
                    maxHeight: "24rem",
                    overflowY: "auto",
                    flexGrow: 1,
                    background:
                      "linear-gradient(135deg, #f8f9fc 0%, #ffffff 100%)",
                  }}
                >
                  <Grid container spacing={0}>
                    {RenderTeamMembersListComponent}
                  </Grid>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ShareAccessToTeamAndUsersModal;
